/**
 * ConnectionService - Manages the WebSocket connection to the backend.
 *
 * This service handles:
 * - Connecting/disconnecting from the server
 * - Connection state tracking and transitions
 * - Sending client messages
 * - Receiving and dispatching server messages
 * - Reconnection logic
 *
 * Uses Socket.io client for transport (matching the backend Socket.io server).
 * The Socket.io client is abstracted via a WebSocketAdapter interface
 * so the service can be tested with mock implementations.
 */

import { io } from 'socket.io-client';
import {
  ConnectionState,
  ConnectionStateChangedEvent,
  ConnectionStateChangeListener,
  isValidTransition,
  isConnectionActive,
} from '../models/ConnectionState';
import {
  ClientToServerMessage,
  ServerToClientMessage,
  validateClientMessage,
  validateServerMessage,
  PROTOCOL_VERSION,
} from '../../shared/messages/MessageContracts';

// ─── WebSocket Adapter Interface ────────────────────────────────────────────

/**
 * Abstract interface for WebSocket-like connections.
 * Allows injection of real Socket.io connection or mock implementations.
 */
export interface WebSocketAdapter {
  url: string;
  readyState: number; // 0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED
  onopen: ((event: Event) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent) => void) | null;
  open(): void;
  close(code?: number, reason?: string): void;
  send(data: string): void;
}

/**
 * Factory function for creating WebSocket adapters.
 * In the browser, this returns a Socket.io-based connection.
 * In tests, this can return a mock.
 */
export type WebSocketFactory = (url: string) => WebSocketAdapter;

/**
 * Create a Socket.io-backed WebSocket adapter.
 * Wraps Socket.io's Socket to match the WebSocketAdapter interface.
 *
 * The backend uses Socket.io, so the frontend must use the Socket.io client
 * to handle the Engine.IO handshake and message framing correctly.
 */
export function createDefaultWebSocket(url: string): WebSocketAdapter {
  // Derive Socket.io options from the URL
  const parsedUrl = new URL(url);

  // Create a Socket.io socket with autoConnect: false (we control lifecycle)
  const ioSocket = io(parsedUrl.origin, {
    path: '/socket.io/',
    transports: ['polling', 'websocket'],
    reconnection: false, // We handle reconnection ourselves
    autoConnect: false,
  });

  // Map Socket.io events to WebSocket-like callbacks
  const adapter: WebSocketAdapter = {
    url: url,
    readyState: 0, // CONNECTING

    onopen: null,
    onclose: null,
    onerror: null,
    onmessage: null,

    open(): void {
      ioSocket.connect();
    },

    close(_code?: number, _reason?: string): void {
      ioSocket.disconnect();
    },

    send(data: string): void {
      // Backend listens on 'message' event and expects JSON string
      ioSocket.emit('message', data);
    },
  };

  // Wire up Socket.io events to adapter callbacks
  ioSocket.on('connect', () => {
    adapter.readyState = 1; // OPEN
    if (adapter.onopen) adapter.onopen(new Event('open'));
  });

  ioSocket.on('disconnect', (reason: string) => {
    adapter.readyState = 3; // CLOSED
    if (adapter.onclose) adapter.onclose(new CloseEvent('close', { reason }));
  });

  ioSocket.on('connect_error', () => {
    adapter.readyState = 3; // CLOSED
    if (adapter.onerror) adapter.onerror(new Event('error'));
  });

  ioSocket.on('error', () => {
    if (adapter.onerror) adapter.onerror(new Event('error'));
  });

  ioSocket.on('message', (data: string) => {
    if (adapter.onmessage) {
      adapter.onmessage(new MessageEvent('message', { data }));
    }
  });

  return adapter;
}

// ─── Server Message Handler ─────────────────────────────────────────────────

/**
 * Callback type for handling received server messages.
 */
export type ServerMessageHandler = (message: ServerToClientMessage) => void;

// ─── Connection Service ─────────────────────────────────────────────────────

export class ConnectionService {
  private _state: ConnectionState = 'disconnected';
  private _socket: WebSocketAdapter | null = null;
  private _url: string;
  private _websocketFactory: WebSocketFactory;
  private _stateListeners: ConnectionStateChangeListener[] = [];
  private _messageHandlers: ServerMessageHandler[] = [];
  private _reconnectAttempts: number = 0;
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _maxReconnectAttempts: number;
  private _reconnectDelayMs: number;

  constructor(
    url: string,
    options?: {
      websocketFactory?: WebSocketFactory;
      maxReconnectAttempts?: number;
      reconnectDelayMs?: number;
    }
  ) {
    this._url = url;
    this._websocketFactory = options?.websocketFactory ?? createDefaultWebSocket;
    // The game must stay connected for the life of the tab: an idle
    // background tab can drop the socket (browser throttling defeats the
    // engine.io ping), and the OLD default gave up after 5 tries (~15 s),
    // leaving the Play/Quick Play buttons disabled forever. Default to
    // unlimited attempts (callers may still cap it explicitly for tests).
    this._maxReconnectAttempts = options?.maxReconnectAttempts ?? Number.POSITIVE_INFINITY;
    this._reconnectDelayMs = options?.reconnectDelayMs ?? 1000;
  }

  /**
   * Force an immediate reconnection attempt if the socket is not currently
   * connected/connecting. Called when the page returns to the foreground
   * (visibilitychange / focus): a background tab may have exhausted its
   * throttled timers while dropped, so recovery should not wait for the
   * next backoff tick — the player should be live the instant they look back.
   * A deliberate `disconnect()` is respected (state === 'disconnected').
   */
  poke(): void {
    if (this._state === 'connected' || this._state === 'connecting' || this._state === 'disconnected') {
      return;
    }
    // reconnecting / error → jump straight to a fresh attempt.
    this._clearReconnectTimer();
    this._connectInternal();
  }

  // ─── Public State Accessors ──────────────────────────────────────────────

  /** Returns the current connection state. */
  get state(): ConnectionState {
    return this._state;
  }

  /** Returns true if the connection is active and usable. */
  get isConnected(): boolean {
    return isConnectionActive(this._state);
  }

  /** Returns the server URL. */
  get url(): string {
    return this._url;
  }

  /** Returns the number of reconnect attempts made. */
  get reconnectAttempts(): number {
    return this._reconnectAttempts;
  }

  // ─── State Change Listeners ──────────────────────────────────────────────

  /**
   * Subscribe to connection state changes.
   * Returns an unsubscribe function.
   */
  onStateChange(listener: ConnectionStateChangeListener): () => void {
    this._stateListeners.push(listener);
    return () => {
      const index = this._stateListeners.indexOf(listener);
      if (index !== -1) {
        this._stateListeners.splice(index, 1);
      }
    };
  }

  // ─── Server Message Handlers ─────────────────────────────────────────────

  /**
   * Subscribe to received server messages.
   * Returns an unsubscribe function.
   */
  onMessage(handler: ServerMessageHandler): () => void {
    this._messageHandlers.push(handler);
    return () => {
      const index = this._messageHandlers.indexOf(handler);
      if (index !== -1) {
        this._messageHandlers.splice(index, 1);
      }
    };
  }

  // ─── Connection Lifecycle ────────────────────────────────────────────────

  /**
   * Establish a connection to the backend server.
   */
  connect(): void {
    if (this._state === 'connecting' || this._state === 'connected') {
      return; // Already connecting or connected
    }

    this._transitionState('connecting');

    try {
      this._socket = this._websocketFactory(this._url);
      this._socket.onopen = () => {
        this._reconnectAttempts = 0;
        this._transitionState('connected');
      };

      this._socket.onclose = (event: CloseEvent) => {
        this._socket = null;
        if (this._state === 'connected') {
          // Mid-connection drop (network jitter, the dev proxy hiccuping,
          // a mobile screen lock stalling pings): attempt a reconnect INSTEAD
          // of giving up. The old code transitioned straight to
          // 'disconnected', which reset the game state and threw the player
          // to the landing page — a refresh was the only way back (the
          // "random coop disconnects" symptom). A deliberate disconnect()
          // is unaffected: it flips the state to 'disconnected' BEFORE the
          // socket closes, so this branch never sees it.
          this._attemptReconnect();
        } else if (this._state === 'connecting' || this._state === 'reconnecting') {
          // Connection attempt failed or reconnection lost
          this._attemptReconnect();
        }
        // If already disconnected, do nothing
      };

      this._socket.onerror = () => {
        if (this._state === 'connecting') {
          this._transitionState('error');
          this._attemptReconnect();
        }
      };

      this._socket.onmessage = (event: MessageEvent) => {
        this._handleServerMessage(event.data);
      };

      this._socket.open();
    } catch (err) {
      this._transitionState('error', err instanceof Error ? err.message : String(err));
      this._attemptReconnect();
    }
  }

  /**
   * Disconnect from the server.
   */
  disconnect(): void {
    this._clearReconnectTimer();
    this._reconnectAttempts = 0;

    if (this._socket) {
      this._socket.close();
      this._socket = null;
    }

    if (this._state !== 'disconnected') {
      this._transitionState('disconnected');
    }
  }

  // ─── Message Sending ─────────────────────────────────────────────────────

  /**
   * Send a client message to the server.
   * Returns true if the message was sent, false if not connected.
   */
  send(message: ClientToServerMessage): boolean {
    if (!this.isConnected || !this._socket) {
      return false;
    }

    try {
      if (!validateClientMessage(message)) {
        return false;
      }
      this._socket.send(JSON.stringify(message));
      return true;
    } catch {
      return false;
    }
  }

  // ─── Internal Methods ────────────────────────────────────────────────────

  private _transitionState(newState: ConnectionState, error?: string): void {
    if (!isValidTransition(this._state, newState)) {
      console.warn(
        `Invalid connection state transition: "${this._state}" -> "${newState}"`
      );
      return;
    }

    const previousState = this._state;
    this._state = newState;

    const event: ConnectionStateChangedEvent = {
      previousState,
      currentState: newState,
      error,
    };

    for (const listener of [...this._stateListeners]) {
      listener(event);
    }
  }

  private _handleServerMessage(rawData: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(rawData);
    } catch {
      console.warn('ConnectionService: Failed to parse server message');
      return;
    }

    if (!validateServerMessage(parsed)) {
      console.warn('ConnectionService: Invalid server message structure');
      return;
    }

    const message = parsed as ServerToClientMessage;
    for (const handler of [...this._messageHandlers]) {
      handler(message);
    }
  }

  private _attemptReconnect(): void {
    if (this._reconnectAttempts >= this._maxReconnectAttempts) {
      this._transitionState('disconnected', 'Max reconnect attempts reached');
      return;
    }

    this._reconnectAttempts++;
    this._transitionState('reconnecting');

    // Exponential-ish backoff, CAPPED (10 s) — with unlimited attempts an
    // unbounded delay would make a later recovery take minutes.
    const delay = this._reconnectDelayMs * Math.min(this._reconnectAttempts, 10);
    this._reconnectTimer = setTimeout(() => {
      this._connectInternal();
    }, delay);
  }

  private _connectInternal(): void {
    if (this._state === 'connecting' || this._state === 'connected') {
      return;
    }

    // Self-transitions (e.g. a poke while already 'reconnecting') are not in
    // VALID_CONNECTION_TRANSITIONS — skip the (rejected) transition instead
    // of logging a spurious warning.
    const target = this._reconnectAttempts > 0 ? 'reconnecting' : 'connecting';
    if (this._state !== target) {
      this._transitionState(target);
    }

    try {
      this._socket = this._websocketFactory(this._url);
      this._socket.onopen = () => {
        this._reconnectAttempts = 0;
        this._transitionState('connected');
      };

      this._socket.onclose = () => {
        this._socket = null;
        this._attemptReconnect();
      };

      this._socket.onerror = () => {
        this._transitionState('error');
        this._attemptReconnect();
      };

      this._socket.onmessage = (event: MessageEvent) => {
        this._handleServerMessage(event.data);
      };

      this._socket.open();
    } catch {
      this._transitionState('error');
      this._attemptReconnect();
    }
  }

  private _clearReconnectTimer(): void {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }
}
